import type { ReactNode } from "react";

export interface SegOption<T extends string | number> {
  value: T;
  label: ReactNode;
}

/**
 * A 2–4-way choice (SNIPPETS §13), over the existing `.seg`: a ground track, 44px buttons, the
 * selected one on paper with a hairline shadow. State is carried by `aria-pressed`, not colour.
 *
 * When to use: switching the view of the same data — Yesterday / Today, 7 / 28 / 90 days.
 * Not for navigation (that is a row) and not for more than four options (that is a sheet).
 */
export function Segmented<T extends string | number>({
  options,
  value,
  onChange,
  ariaLabel,
}: {
  options: SegOption<T>[];
  /** null: nothing chosen yet — a question not answered, where no default would be honest */
  value: T | null;
  onChange: (value: T) => void;
  ariaLabel: string;
}) {
  return (
    <div className="seg k-seg" role="group" aria-label={ariaLabel}>
      {options.map((o) => {
        const on = o.value === value;
        return (
          <button
            key={String(o.value)}
            type="button"
            className={on ? "on" : ""}
            aria-pressed={on}
            onClick={() => onChange(o.value)}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}
